import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr } from "../api";
import { onAuthedFileClick } from "../mobileFiles";
import { Modal, fmtEur, CourseImagePicker } from "./AdminSections";
import {
  GraduationCap, Users, FileText, Wallet, Plus, Trash2, Upload, Download,
  MessageSquare, Award, ChevronLeft, Pencil,
} from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;
const fileHref = (u) => (!u ? "#" : u.startsWith("http") ? u : `${BE}${u}`);
const inp = "w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm";
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const rid = (p) => `rk_${Date.now()}_${Math.random().toString(16).slice(2, 8)}_${p}`;

const readFileDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

/* ============================ ROOT ============================ */
export default function AcademyTab() {
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selected, setSelected] = useState(null); // course id in detail view
  const [form, setForm] = useState(null);

  const loadCourses = useCallback(() => {
    api.get("/admin/academy/courses").then((r) => setCourses(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  const loadTeachers = useCallback(() => {
    api.get("/admin/academy/users?role=teacher").then((r) => setTeachers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadCourses(); loadTeachers(); }, [loadCourses, loadTeachers]);

  if (selected) {
    return <CourseDetail cid={selected} teachers={teachers} onBack={() => { setSelected(null); loadCourses(); }} />;
  }

  return (
    <div data-testid="academy-tab">
      <div className="flex items-center gap-2 mb-3 text-violet-700">
        <GraduationCap size={18} /><span className="text-sm font-semibold">Corsi MAS Academy</span>
      </div>
      <button data-testid="academy-course-new" onClick={() => setForm({})}
        className="btn-primary text-sm w-full mb-4 inline-flex items-center justify-center gap-2">
        <Plus size={16} /> Nuovo corso Academy
      </button>

      <div className="space-y-3">
        {courses.map((c) => {
          const t = teachers.find((x) => x.id === c.teacher_user_id);
          return (
            <div key={c.id} className="card p-3" data-testid={`academy-course-${c.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{c.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {fmtEur(c.price)} · {c.duration_days ? `${c.duration_days} giornate` : "durata n.d."} · {c.days_count || 0} giornate create
                  </div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    Corso MAS {c.mas_pct ?? "—"}% / Docente {c.teacher_pct ?? "—"}% · Docente: {t ? t.name : (c.teacher_user_id ? "assegnato" : "non assegnato")}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    Tutoraggio {c.tutoring_enabled ? `sì (${c.tutoring_duration_days || 90}gg)` : "no"} · Assistenza {c.assistance_enabled ? `sì (${fmtEur(c.assistance_price)}/mese)` : "no"} · Level Up {c.level_up_enabled ? "sì" : "no"}
                  </div>
                </div>
                <span className={`badge-chip shrink-0 ${c.active === false ? "state-COMPLETO" : "state-DISPONIBILE"}`}>{c.active === false ? "non attivo" : "attivo"}</span>
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">
                <button data-testid={`academy-manage-${c.id}`} onClick={() => setSelected(c.id)} className="btn-primary text-xs py-1 px-2">Gestisci</button>
                <button data-testid={`academy-edit-${c.id}`} onClick={() => setForm(c)} className="btn-outline text-xs py-1 px-2 inline-flex items-center gap-1"><Pencil size={12} /> Dati e prezzi</button>
              </div>
            </div>
          );
        })}
        {courses.length === 0 && <p className="text-sm text-gray-400">Nessun corso Academy. Creane uno.</p>}
      </div>

      <CourseFormModal open={!!form} initial={form} teachers={teachers}
        onClose={() => setForm(null)} onSaved={() => { setForm(null); loadCourses(); }} />
    </div>
  );
}

/* ============================ COURSE FORM ============================ */
function CourseFormModal({ open, initial, teachers, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const c = initial || {};
    setF({
      title: c.title || "", description: c.description || "", duration_days: c.duration_days || "",
      spots: c.spots ?? 10, image: c.image || "",
      price: c.price ?? 0, mas_pct: c.mas_pct ?? 40, teacher_pct: c.teacher_pct ?? 60,
      tutoring_enabled: !!c.tutoring_enabled, tutoring_duration_days: c.tutoring_duration_days || 90,
      level_up_enabled: !!c.level_up_enabled,
      assistance_enabled: !!c.assistance_enabled, assistance_price: c.assistance_price ?? 0,
      support_mas_pct: c.support_mas_pct ?? 70, support_teacher_pct: c.support_teacher_pct ?? 30,
      active: c.active !== false, teacher_user_id: c.teacher_user_id || "", id: c.id,
    });
  }, [open, initial]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    if (!f.title.trim()) return toast.error("Inserisci il titolo del corso");
    setBusy(true);
    const body = {
      title: f.title.trim(), description: f.description, active: f.active,
      duration_days: num(f.duration_days), price: num(f.price) || 0, spots: num(f.spots) || 1, image: f.image || "",
      mas_pct: num(f.mas_pct), teacher_pct: num(f.teacher_pct),
      tutoring_enabled: f.tutoring_enabled, tutoring_duration_days: num(f.tutoring_duration_days),
      level_up_enabled: f.level_up_enabled,
      assistance_enabled: f.assistance_enabled, assistance_price: num(f.assistance_price) || 0,
      support_mas_pct: num(f.support_mas_pct), support_teacher_pct: num(f.support_teacher_pct),
    };
    try {
      let cid = f.id;
      if (cid) await api.put(`/admin/academy/courses/${cid}`, body);
      else { const r = await api.post("/admin/academy/courses", body); cid = r.data.id; }
      await api.put(`/admin/academy/courses/${cid}/teacher`, { teacher_user_id: f.teacher_user_id || "" });
      toast.success(f.id ? "Corso aggiornato" : "Corso creato");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={f.id ? "Modifica corso Academy" : "Nuovo corso Academy"}>
      <div className="space-y-2">
        <label className="block text-xs text-gray-500">Titolo<input data-testid="ac-title" className={inp} value={f.title || ""} onChange={(e) => set("title", e.target.value)} /></label>
        <label className="block text-xs text-gray-500">Descrizione<textarea data-testid="ac-desc" className={inp} rows={2} value={f.description || ""} onChange={(e) => set("description", e.target.value)} /></label>
        <div className="rounded-xl bg-gray-50 p-2" data-testid="ac-cover">
          <CourseImagePicker value={f.image} onChange={(url) => set("image", url)} label="Copertina del corso (dal dispositivo)" />
          {f.image && <button type="button" data-testid="ac-cover-remove" onClick={() => set("image", "")} className="text-xs text-red-600 mt-2">Rimuovi copertina</button>}
        </div>
        <div className="flex gap-2">
          <label className="block text-xs text-gray-500 flex-1">Durata (giornate)<input data-testid="ac-duration" type="number" min="1" className={inp} value={f.duration_days || ""} onChange={(e) => set("duration_days", e.target.value)} /></label>
          <label className="block text-xs text-gray-500 flex-1">Prezzo corso (€)<input data-testid="ac-price" type="number" min="0" step="0.01" className={inp} value={f.price} onChange={(e) => set("price", e.target.value)} /></label>
        </div>
        <label className="block text-xs text-gray-500">Numero massimo partecipanti<input data-testid="ac-spots" type="number" min="1" className={inp} value={f.spots} onChange={(e) => set("spots", e.target.value)} /></label>

        <div className="rounded-xl bg-gray-50 p-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Ripartizione corso</div>
          <div className="flex gap-2">
            <label className="block text-xs text-gray-500 flex-1">Quota MAS %<input data-testid="ac-mas" type="number" min="0" max="100" className={inp} value={f.mas_pct} onChange={(e) => set("mas_pct", e.target.value)} /></label>
            <label className="block text-xs text-gray-500 flex-1">Quota docente %<input data-testid="ac-teacher" type="number" min="0" max="100" className={inp} value={f.teacher_pct} onChange={(e) => set("teacher_pct", e.target.value)} /></label>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Le due percentuali devono sommare 100.</p>
        </div>

        <label className="block text-xs text-gray-500">Docente assegnato
          <select data-testid="ac-teacher-select" className={inp} value={f.teacher_user_id || ""} onChange={(e) => set("teacher_user_id", e.target.value)}>
            <option value="">— nessuno —</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm mt-1"><input data-testid="ac-tutoring" type="checkbox" checked={f.tutoring_enabled} onChange={(e) => set("tutoring_enabled", e.target.checked)} />Tutoraggio iniziale incluso</label>
        {f.tutoring_enabled && <label className="block text-xs text-gray-500">Durata tutoraggio (giorni)<input data-testid="ac-tutoring-days" type="number" min="1" className={inp} value={f.tutoring_duration_days} onChange={(e) => set("tutoring_duration_days", e.target.value)} /></label>}
        <label className="flex items-center gap-2 text-sm"><input data-testid="ac-levelup" type="checkbox" checked={f.level_up_enabled} onChange={(e) => set("level_up_enabled", e.target.checked)} />Abilita attestato Level Up</label>

        <div className="rounded-xl bg-gray-50 p-2">
          <label className="flex items-center gap-2 text-sm"><input data-testid="ac-assist" type="checkbox" checked={f.assistance_enabled} onChange={(e) => set("assistance_enabled", e.target.checked)} />Assistenza post-corso a pagamento</label>
          {f.assistance_enabled && <div className="mt-2 space-y-2">
            <label className="block text-xs text-gray-500">Prezzo assistenza (€/mese, anche 0)<input data-testid="ac-assist-price" type="number" min="0" step="0.01" className={inp} value={f.assistance_price} onChange={(e) => set("assistance_price", e.target.value)} /></label>
            <div className="flex gap-2">
              <label className="block text-xs text-gray-500 flex-1">Quota MAS assistenza %<input data-testid="ac-assist-mas" type="number" min="0" max="100" className={inp} value={f.support_mas_pct} onChange={(e) => set("support_mas_pct", e.target.value)} /></label>
              <label className="block text-xs text-gray-500 flex-1">Quota docente assistenza %<input data-testid="ac-assist-teacher" type="number" min="0" max="100" className={inp} value={f.support_teacher_pct} onChange={(e) => set("support_teacher_pct", e.target.value)} /></label>
            </div>
          </div>}
        </div>

        <label className="flex items-center gap-2 text-sm"><input data-testid="ac-active" type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} />Attivo (visibile e iscrivibile)</label>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="ac-save" disabled={busy} onClick={save} className="btn-primary flex-1 text-sm">{busy ? "Salvo…" : "Salva"}</button>
        </div>
      </div>
    </Modal>
  );
}

/* ============================ COURSE DETAIL ============================ */
function CourseDetail({ cid, teachers, onBack }) {
  const [detail, setDetail] = useState(null);
  const [sub, setSub] = useState("giornate");
  const load = useCallback(() => {
    api.get(`/admin/academy/courses/${cid}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  if (!detail) return <div className="text-center text-gray-400 pt-10 text-sm">Carico…</div>;
  const SUBS = [["giornate", "Giornate & Materiali"], ["esercizi", "Esercizi"], ["iscritti", "Iscritti & Contabilità"]];

  return (
    <div data-testid="academy-detail">
      <button data-testid="academy-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-violet-700 mb-3"><ChevronLeft size={16} /> Torna ai corsi</button>
      <div className="card p-3 mb-3">
        <div className="font-semibold text-sm">{detail.title}</div>
        <div className="text-xs text-gray-500 mt-0.5">{fmtEur(detail.price)} · MAS {detail.mas_pct}% / Docente {detail.teacher_pct}%</div>
      </div>
      <select data-testid="academy-subsection" value={sub} onChange={(e) => setSub(e.target.value)}
        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-medium bg-white mb-4">
        {SUBS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>

      {sub === "giornate" && <DaysManager cid={cid} days={detail.days || []} reload={load} />}
      {sub === "esercizi" && <ExercisesManager cid={cid} days={detail.days || []} />}
      {sub === "iscritti" && <EnrollmentsManager course={detail} />}
    </div>
  );
}

/* ---------- Giornate & Materiali ---------- */
function DaysManager({ cid, days, reload }) {
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Titolo giornata obbligatorio");
    setBusy(true);
    try {
      if (form.id) await api.put(`/admin/academy/days/${form.id}`, form);
      else await api.post(`/admin/academy/courses/${cid}/days`, form);
      toast.success("Giornata salvata"); setForm(null); reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const uploadMaterial = async (dayId, file) => {
    const ok = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!ok.includes(file.type)) return toast.error("Usa PDF, JPG, PNG o WEBP");
    const max = file.type === "application/pdf" ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > max) return toast.error(`File troppo grande (max ${file.type === "application/pdf" ? "20" : "5"} MB)`);
    try {
      const data = await readFileDataUrl(file);
      await api.post(`/admin/academy/days/${dayId}/materials`, { filename: file.name, data, kind: "material" });
      toast.success("Materiale caricato"); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="days-manager">
      <button data-testid="day-new" onClick={() => setForm({ title: "" })} className="btn-primary text-sm w-full mb-3 inline-flex items-center justify-center gap-2"><Plus size={16} /> Aggiungi giornata</button>
      <div className="space-y-3">
        {days.map((d) => (
          <div key={d.id} className="card p-3" data-testid={`day-${d.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm">Giornata {(d.day_index ?? 0) + 1} · {d.title}</div>
                {d.description && <div className="text-xs text-gray-500 mt-0.5">{d.description}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`day-edit-${d.id}`} onClick={() => setForm(d)} className="btn-outline text-xs py-1 px-2">Modifica</button>
                <button data-testid={`day-del-${d.id}`} onClick={() => setDel(d)} className="btn-outline text-xs py-1 px-2"><Trash2 size={12} /></button>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              {(d.materials || []).map((m) => (
                <div key={m.id} className="flex justify-between items-center text-[11px] bg-gray-50 rounded-lg px-2 py-1" data-testid={`material-${m.id}`}>
                  <span className="truncate min-w-0"><FileText size={11} className="inline mr-1" />{m.filename}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <a href={fileHref(`/api/academy/materials/${m.id}/file`)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, `/api/academy/materials/${m.id}/file`, m.filename)} className="text-violet-700"><Download size={13} /></a>
                    <button data-testid={`material-del-${m.id}`} onClick={async () => { try { await api.delete(`/admin/academy/materials/${m.id}`); toast.success("Materiale eliminato"); reload(); } catch (e) { toast.error(apiErr(e)); } }} className="text-gray-400"><Trash2 size={12} /></button>
                  </span>
                </div>
              ))}
              {(d.materials || []).length === 0 && <div className="text-[11px] text-gray-400">Nessun materiale.</div>}
              <label className="btn-outline text-xs cursor-pointer inline-flex items-center gap-1 mt-1" data-testid={`material-add-${d.id}`}>
                <Upload size={12} /> Carica PDF/immagine
                <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadMaterial(d.id, file); e.target.value = ""; }} />
              </label>
            </div>
          </div>
        ))}
        {days.length === 0 && <p className="text-sm text-gray-400">Nessuna giornata. Aggiungine una.</p>}
      </div>

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Modifica giornata" : "Nuova giornata"}>
        {form && <div className="space-y-2">
          <label className="block text-xs text-gray-500">Titolo<input data-testid="day-title" className={inp} value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label className="block text-xs text-gray-500">Descrizione<textarea className={inp} rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
          <label className="block text-xs text-gray-500">Programma<textarea className={inp} rows={2} value={form.program || ""} onChange={(e) => setForm({ ...form, program: e.target.value })} /></label>
          <label className="block text-xs text-gray-500">Istruzioni<textarea className={inp} rows={2} value={form.instructions || ""} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></label>
          <div className="flex gap-2 pt-1">
            <button onClick={() => setForm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
            <button data-testid="day-save" disabled={busy} onClick={save} className="btn-primary flex-1 text-sm">Salva</button>
          </div>
        </div>}
      </Modal>

      <Modal open={!!del} onClose={() => setDel(null)} title="Eliminare questa giornata?">
        <p className="text-sm text-gray-600 mb-3">Verranno eliminati anche i materiali della giornata. Operazione non annullabile.</p>
        <div className="flex gap-2">
          <button onClick={() => setDel(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="day-del-confirm" onClick={async () => { const d = del; setDel(null); try { await api.delete(`/admin/academy/days/${d.id}`); toast.success("Giornata eliminata"); reload(); } catch (e) { toast.error(apiErr(e)); } }} className="btn-primary flex-1 text-sm bg-red-600 border-red-600">Elimina</button>
        </div>
      </Modal>
    </div>
  );
}

/* ---------- Esercizi ---------- */
function ExercisesManager({ cid, days }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => { api.get(`/academy/courses/${cid}/exercises`).then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e))); }, [cid]);
  useEffect(() => { load(); }, [load]);
  const dayLabel = (id) => { const d = days.find((x) => x.id === id); return d ? `G${(d.day_index ?? 0) + 1} · ${d.title}` : "—"; };

  const save = async () => {
    if (!form.day_id) return toast.error("Seleziona la giornata");
    if (!form.title?.trim()) return toast.error("Titolo esercizio obbligatorio");
    try {
      if (form.id) await api.put(`/academy/exercises/${form.id}`, { title: form.title, instructions: form.instructions, required: form.required, active: form.active });
      else await api.post(`/academy/courses/${cid}/exercises`, { day_id: form.day_id, title: form.title, instructions: form.instructions, required: form.required, active: form.active });
      toast.success("Esercizio salvato"); setForm(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="exercises-manager">
      <button data-testid="exercise-new" disabled={days.length === 0} onClick={() => setForm({ day_id: days[0]?.id || "", title: "", instructions: "", required: true, active: true })} className="btn-primary text-sm w-full mb-3 inline-flex items-center justify-center gap-2"><Plus size={16} /> Nuovo esercizio</button>
      {days.length === 0 && <p className="text-xs text-amber-600 mb-2">Crea prima almeno una giornata.</p>}
      <div className="space-y-2">
        {list.map((x) => (
          <div key={x.id} className="card p-3" data-testid={`exercise-${x.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm">{x.title}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{dayLabel(x.day_id)} · {x.required ? "obbligatorio" : "facoltativo"} · {x.active === false ? "non attivo" : "attivo"}</div>
                {x.instructions && <div className="text-xs text-gray-500 mt-0.5">{x.instructions}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`exercise-edit-${x.id}`} onClick={() => setForm(x)} className="btn-outline text-xs py-1 px-2">Modifica</button>
                <button data-testid={`exercise-del-${x.id}`} onClick={() => setDel(x)} className="btn-outline text-xs py-1 px-2"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-gray-400">Nessun esercizio.</p>}
      </div>

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Modifica esercizio" : "Nuovo esercizio"}>
        {form && <div className="space-y-2">
          <label className="block text-xs text-gray-500">Giornata
            <select data-testid="exercise-day" className={inp} value={form.day_id || ""} disabled={!!form.id} onChange={(e) => setForm({ ...form, day_id: e.target.value })}>
              {days.map((d) => <option key={d.id} value={d.id}>G{(d.day_index ?? 0) + 1} · {d.title}</option>)}
            </select>
          </label>
          <label className="block text-xs text-gray-500">Titolo<input data-testid="exercise-title" className={inp} value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label className="block text-xs text-gray-500">Istruzioni<textarea className={inp} rows={2} value={form.instructions || ""} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.required !== false} onChange={(e) => setForm({ ...form, required: e.target.checked })} />Obbligatorio (conta per il Level Up)</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />Attivo</label>
          <div className="flex gap-2 pt-1">
            <button onClick={() => setForm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
            <button data-testid="exercise-save" onClick={save} className="btn-primary flex-1 text-sm">Salva</button>
          </div>
        </div>}
      </Modal>

      <Modal open={!!del} onClose={() => setDel(null)} title="Eliminare l'esercizio?">
        <p className="text-sm text-gray-600 mb-3">Verranno eliminate anche le consegne collegate. Operazione non annullabile.</p>
        <div className="flex gap-2">
          <button onClick={() => setDel(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="exercise-del-confirm" onClick={async () => { const x = del; setDel(null); try { await api.delete(`/academy/exercises/${x.id}`); toast.success("Esercizio eliminato"); load(); } catch (e) { toast.error(apiErr(e)); } }} className="btn-primary flex-1 text-sm bg-red-600 border-red-600">Elimina</button>
        </div>
      </Modal>
    </div>
  );
}

/* ---------- Iscritti & Contabilità ---------- */
function EnrollmentsManager({ course }) {
  const cid = course.id;
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null); // enrollment id expanded
  const load = useCallback(() => { api.get(`/admin/academy/courses/${cid}/enrollments`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, [cid]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="enrollments-manager">
      <div className="flex items-center gap-2 mb-2 text-violet-700"><Users size={16} /><span className="text-sm font-semibold">{rows.length} iscritti</span></div>
      <div className="space-y-3">
        {rows.map((e) => (
          <div key={e.id} className="card p-3" data-testid={`enrollment-${e.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{e.name || "—"}</div>
                <div className="text-[11px] text-gray-500 truncate">{e.email}</div>
              </div>
              <span className={`badge-chip shrink-0 ${e.status === "paid" ? "state-DISPONIBILE" : e.status === "refunded" ? "state-COMPLETO" : "state-ULTIMA"}`}>{e.status === "paid" ? "pagato" : e.status === "refunded" ? "rimborsato" : "in attesa"}</span>
            </div>
            <button data-testid={`enrollment-toggle-${e.id}`} onClick={() => setOpen(open === e.id ? null : e.id)} className="btn-outline text-xs py-1 px-2 mt-2">{open === e.id ? "Chiudi" : "Contabilità e gestione"}</button>
            {open === e.id && <EnrollmentPanel enr={e} course={course} reload={load} />}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-gray-400">Nessun iscritto.</p>}
      </div>
    </div>
  );
}

function LedgerCard({ lv }) {
  if (!lv) return <div className="text-[11px] text-gray-400">Contabilità disponibile dopo il pagamento.</div>;
  const clawbacks = (lv.adjustments || []).filter((a) => a.type === "teacher_clawback");
  return (
    <div className="rounded-xl bg-gray-50 p-3 text-xs space-y-1" data-testid="ledger-card">
      <Line label="Incassato" value={fmtEur(lv.amount)} />
      <Line label="Quota MAS" value={fmtEur(lv.mas_amount)} />
      <Line label="Quota docente maturata" value={fmtEur(lv.teacher_accrued)} />
      <Line label="Corrisposta" value={fmtEur(lv.teacher_paid)} />
      <Line label="Da corrispondere" value={fmtEur(Math.max(0, lv.teacher_due))} strong />
      {lv.refund && <div className="text-[11px] text-gray-500 pt-1 border-t border-gray-200">Rimborso: {fmtEur(lv.refund.amount)} · trattenuto {fmtEur(lv.refund.retained)}</div>}
      {clawbacks.map((a, i) => (
        <div key={i} className="text-[11px] text-red-600 pt-1 border-t border-gray-200" data-testid="clawback-line">
          Rettifica da recuperare/compensare: {fmtEur(a.amount)}
          <div className="text-[10px] text-gray-500">{a.note}</div>
        </div>
      ))}
    </div>
  );
}
const Line = ({ label, value, strong }) => (
  <div className="flex justify-between"><span className="text-gray-500">{label}</span><span className={strong ? "font-semibold text-gray-900" : "text-gray-800"}>{value}</span></div>
);

function EnrollmentPanel({ enr, course, reload }) {
  const eid = enr.id;
  const [markPaid, setMarkPaid] = useState(null); // {kind:'course'|'assistance', id, due}
  const [assist, setAssist] = useState([]);
  const [refund, setRefund] = useState(null);
  const [certs, setCerts] = useState([]);
  const [tut, setTut] = useState(null);
  const paid = enr.status === "paid";

  const loadAssist = useCallback(() => { api.get(`/admin/academy/enrollments/${eid}/assistance`).then((r) => setAssist(r.data)).catch(() => {}); }, [eid]);
  const loadCerts = useCallback(() => { api.get(`/academy/enrollments/${eid}/certificates`).then((r) => setCerts(r.data)).catch(() => {}); }, [eid]);
  useEffect(() => { if (paid) { loadAssist(); loadCerts(); } }, [paid, loadAssist, loadCerts]);

  const doMarkPaid = async (amount, note) => {
    const t = markPaid; setMarkPaid(null);
    const url = t.kind === "course" ? `/admin/academy/enrollments/${eid}/mark-teacher-paid` : `/admin/academy/assistance/${t.id}/mark-teacher-paid`;
    try { await api.post(url, { amount: amount || undefined, note, payout_key: rid(t.kind) }); toast.success("Quota segnata come corrisposta"); reload(); loadAssist(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openTutoring = async () => {
    try {
      const r = await api.get(`/academy/enrollments/${eid}/tutoring`);
      const thread = r.data.thread;
      let msgs = [];
      if (thread?.id) { const m = await api.get(`/academy/tutoring/${thread.id}/messages`); msgs = m.data; }
      setTut({ policy: r.data.tutoring, thread, msgs });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const issueCert = async (type) => {
    try {
      if (type === "frequency") await api.post(`/academy/enrollments/${eid}/certificate/frequency`);
      else await api.post(`/academy/enrollments/${eid}/certificate/level-up`, { confirm: true });
      toast.success("Attestato generato"); loadCerts();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3" data-testid={`enrollment-panel-${eid}`}>
      <div>
        <div className="flex items-center gap-2 mb-1 text-gray-700"><Wallet size={14} /><span className="text-xs font-semibold uppercase tracking-wide">Contabilità corso</span></div>
        <LedgerCard lv={enr.ledger_view} />
        {paid && enr.ledger_view && (enr.ledger_view.teacher_due || 0) > 0 &&
          <button data-testid={`markpaid-course-${eid}`} onClick={() => setMarkPaid({ kind: "course", due: enr.ledger_view.teacher_due })} className="btn-primary text-xs w-full mt-2">Segna come corrisposta</button>}
      </div>

      {paid && <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Assistenza post-corso</div>
        {assist.length === 0 && <div className="text-[11px] text-gray-400">Nessun periodo di assistenza.</div>}
        {assist.map((p) => (
          <div key={p.id} className="rounded-xl border border-gray-100 p-2 mb-2" data-testid={`assist-${p.id}`}>
            <div className="text-[11px] text-gray-600 flex justify-between">
              <span>{fmtD(p.start_at)} → {fmtD(p.end_at)}</span>
              <span className={p.status === "active" || p.status === "free" ? "text-green-600" : p.status === "pending" ? "text-amber-600" : "text-gray-500"}>{p.status === "cancelled" ? "annullato/rimborsato" : p.status === "free" ? "gratuito" : p.status === "pending" ? "in attesa di pagamento" : p.status}</span>
            </div>
            {p.ledger_view && <div className="mt-1"><LedgerCard lv={p.ledger_view} /></div>}
            <div className="flex gap-1 mt-2 flex-wrap">
              {(p.status === "active" || p.status === "free") && !p.refunded_at && <button data-testid={`assist-refund-${p.id}`} onClick={() => setRefund(p)} className="btn-outline text-xs py-1 px-2">Rimborso</button>}
              {p.ledger_view && (p.ledger_view.teacher_due || 0) > 0 && <button data-testid={`markpaid-assist-${p.id}`} onClick={() => setMarkPaid({ kind: "assistance", id: p.id, due: p.ledger_view.teacher_due })} className="btn-primary text-xs py-1 px-2">Segna come corrisposta</button>}
            </div>
          </div>
        ))}
      </div>}

      {paid && <div>
        <div className="flex items-center gap-2 mb-1 text-gray-700"><Award size={14} /><span className="text-xs font-semibold uppercase tracking-wide">Attestati</span></div>
        <div className="space-y-1">
          {certs.map((c) => (
            <div key={c.id} className="flex justify-between items-center text-[11px] bg-gray-50 rounded-lg px-2 py-1">
              <span>{c.type === "frequency" ? "Frequenza" : "Level Up"} · {c.certificate_number}</span>
              <a href={fileHref(c.file_url)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, c.file_url, `Attestato-${c.certificate_number || "MAS"}.pdf`)} className="text-violet-700 inline-flex items-center gap-1"><Download size={12} /> PDF</a>
            </div>
          ))}
          {certs.length === 0 && <div className="text-[11px] text-gray-400">Nessun attestato generato.</div>}
        </div>
        <div className="flex gap-1 mt-2 flex-wrap">
          <button data-testid={`cert-freq-${eid}`} onClick={() => issueCert("frequency")} className="btn-outline text-xs py-1 px-2">Attestato di frequenza</button>
          {course.level_up_enabled && <button data-testid={`cert-levelup-${eid}`} onClick={() => issueCert("level_up")} className="btn-outline text-xs py-1 px-2">Attestato Level Up</button>}
        </div>
      </div>}

      {paid && <div>
        <div className="flex items-center gap-2 mb-1 text-gray-700"><MessageSquare size={14} /><span className="text-xs font-semibold uppercase tracking-wide">Tutoraggio (supervisione)</span></div>
        <button data-testid={`tutoring-open-${eid}`} onClick={openTutoring} className="btn-outline text-xs py-1 px-2">Apri thread</button>
      </div>}

      <MarkPaidModal open={!!markPaid} due={markPaid?.due} onClose={() => setMarkPaid(null)} onSubmit={doMarkPaid} />
      <AssistanceRefundModal period={refund} onClose={() => setRefund(null)} onDone={() => { setRefund(null); loadAssist(); reload(); }} />
      <Modal open={!!tut} onClose={() => setTut(null)} title="Thread di tutoraggio">
        {tut && <div className="space-y-2">
          <div className="text-[11px] text-gray-500">Stato: {tut.policy?.enabled ? tut.policy.status : "non abilitato"}</div>
          <div className="max-h-[50vh] overflow-y-auto space-y-2" data-testid="tutoring-messages">
            {(tut.msgs || []).map((m) => (
              <div key={m.id} className="text-xs rounded-xl bg-gray-50 p-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{m.sender_role === "student" ? "Allievo" : m.sender_role === "teacher" ? "Docente" : "MAS"} · {fmtD(m.created_at)}</div>
                {m.text && <div className="text-gray-800 mt-0.5 whitespace-pre-wrap">{m.text}</div>}
                {(m.attachments || []).map((a, i) => <a key={i} href={fileHref(a.file_url)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, a.file_url, a.filename)} className="text-violet-700 inline-flex items-center gap-1 mt-1"><Download size={11} /> {a.filename || "allegato"}</a>)}
              </div>
            ))}
            {(tut.msgs || []).length === 0 && <div className="text-[11px] text-gray-400">Nessun messaggio.</div>}
          </div>
        </div>}
      </Modal>
    </div>
  );
}

function MarkPaidModal({ open, due, onClose, onSubmit }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setAmount(""); setNote(""); } }, [open]);
  return (
    <Modal open={open} onClose={onClose} title="Segna quota docente come corrisposta">
      <div className="space-y-2 text-sm">
        <div className="text-gray-600">Da corrispondere: <b>{fmtEur(due || 0)}</b></div>
        <label className="block text-xs text-gray-500">Importo corrisposto (€) — vuoto = intero da corrispondere<input data-testid="markpaid-amount" type="number" min="0" step="0.01" className={inp} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(due || 0)} /></label>
        <label className="block text-xs text-gray-500">Nota / riferimento (facoltativo)<input data-testid="markpaid-note" className={inp} value={note} onChange={(e) => setNote(e.target.value)} /></label>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="markpaid-confirm" onClick={() => onSubmit(amount ? Number(amount) : null, note)} className="btn-primary flex-1 text-sm">Conferma</button>
        </div>
      </div>
    </Modal>
  );
}

function AssistanceRefundModal({ period, onClose, onDone }) {
  const [mode, setMode] = useState("ordinary"); // ordinary | override
  const [basis, setBasis] = useState("pct"); // pct | amount
  const [pct, setPct] = useState(100);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [keepAccess, setKeepAccess] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (period) { setMode("ordinary"); setBasis("pct"); setPct(100); setAmount(""); setReason(""); setKeepAccess(true); } }, [period]);
  if (!period) return null;
  const started = period.start_at && new Date(period.start_at) <= new Date();

  const submit = async () => {
    let body = {};
    if (mode === "override") {
      if (!reason.trim()) return toast.error("Motivazione obbligatoria per il rimborso eccezionale");
      body = { override: true, reason: reason.trim(), keep_access: keepAccess };
      if (basis === "amount") { if (amount === "") return toast.error("Inserisci l'importo"); body.amount = Number(amount); }
      else body.pct = Number(pct);
    }
    setBusy(true);
    try { const { data } = await api.post(`/admin/academy/assistance/${period.id}/refund`, body); if (data && data.already) toast.info("Questo periodo risulta già rimborsato."); else toast.success("Rimborso elaborato"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={!!period} onClose={onClose} title="Rimborso assistenza">
      <div className="space-y-2 text-sm">
        <div className="rounded-xl bg-gray-50 p-2 text-xs text-gray-600">
          Prezzo periodo: <b>{fmtEur(period.price)}</b> · {started ? "periodo già iniziato" : "periodo non ancora iniziato"}
        </div>
        <label className="block text-xs text-gray-500">Tipo rimborso
          <select data-testid="refund-mode" className={inp} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="ordinary">Ordinario (policy A8)</option>
            <option value="override">Eccezione Admin (servizio non erogabile / straordinario)</option>
          </select>
        </label>
        {mode === "ordinary" && <p className="text-xs text-gray-500">{started
          ? "Periodo già iniziato: rimborso ordinario 0%. L'accesso resta valido fino alla scadenza naturale."
          : "Periodo non ancora iniziato: rimborso 100% e periodo annullato (accesso revocato). Lo storico resta conservato."}</p>}

        {mode === "override" && <div className="space-y-2">
          <label className="block text-xs text-gray-500">Base
            <select data-testid="refund-basis" className={inp} value={basis} onChange={(e) => setBasis(e.target.value)}>
              <option value="pct">Percentuale</option><option value="amount">Importo</option>
            </select>
          </label>
          {basis === "pct"
            ? <label className="block text-xs text-gray-500">Percentuale (0–100)<input data-testid="refund-pct" type="number" min="0" max="100" className={inp} value={pct} onChange={(e) => setPct(e.target.value)} /></label>
            : <label className="block text-xs text-gray-500">Importo (€)<input data-testid="refund-amount" type="number" min="0" step="0.01" className={inp} value={amount} onChange={(e) => setAmount(e.target.value)} /></label>}
          <label className="block text-xs text-gray-500">Motivazione (obbligatoria)<textarea data-testid="refund-reason" className={inp} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
          <label className="flex items-center gap-2 text-sm"><input data-testid="refund-keepaccess" type="checkbox" checked={keepAccess} onChange={(e) => setKeepAccess(e.target.checked)} />Mantieni l'accesso fino a scadenza (solo rimborso parziale)</label>
        </div>}

        <p className="text-xs text-red-600 font-medium">Il rimborso viene eseguito tramite Stripe. Ricalcolo automatico di quota MAS e quota docente sulla parte trattenuta; eventuale rettifica docente da recuperare/compensare.</p>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="refund-confirm" disabled={busy} onClick={submit} className="btn-primary flex-1 text-sm">{busy ? "Elaboro…" : "Conferma rimborso"}</button>
        </div>
      </div>
    </Modal>
  );
}
